/**
 * QuestionPrompt — multi-choice question UI for agent clarifying questions
 *
 * @sync OpenCode → packages/opencode/src/cli/cmd/tui/routes/session/index.tsx (question prompts)
 *
 * Visual pattern matches PermissionPrompt (bordered card with action buttons),
 * but uses blue/purple tones instead of yellow.
 * Includes an "Other" free-text input option for each question.
 */
import { useState, useCallback, useRef } from 'react'
import { useTranslation } from '@papan/i18n'
import { HelpCircle, Send, X, MessageSquare } from 'lucide-react'
import type { QuestionRequest } from './hooks.js'
import { promptAgeDays } from './PermissionPrompt.js'

interface Props {
  request: QuestionRequest
  onReply: (answers: string[][]) => void
  onDismiss: () => void
}

const OTHER_LABEL = '__other__'

export function QuestionPrompt({ request, onReply, onDismiss }: Props) {
  const { t } = useTranslation('agent')
  const ageDays = promptAgeDays(request.askedAt) // #648: a question can wait for days
  // One array of selected labels per question
  const [selected, setSelected] = useState<string[][]>(
    () => request.questions.map(() => [])
  )
  // Free-text input per question (used when "Other" is selected)
  const [otherText, setOtherText] = useState<string[]>(
    () => request.questions.map(() => '')
  )
  const otherRefs = useRef<(HTMLInputElement | null)[]>([])

  const toggle = useCallback((qi: number, label: string, multiple: boolean) => {
    setSelected(prev => {
      const next = [...prev]
      if (multiple) {
        const arr = next[qi]
        next[qi] = arr.includes(label) ? arr.filter(l => l !== label) : [...arr, label]
      } else {
        next[qi] = [label]
      }
      return next
    })
    // Auto-focus "Other" text input when selected
    if (label === OTHER_LABEL) {
      setTimeout(() => otherRefs.current[qi]?.focus(), 0)
    }
  }, [])

  const handleSubmit = useCallback(() => {
    // Resolve "Other" selections to their text values
    const resolved = selected.map((answers, qi) =>
      answers.map(label => {
        if (label === OTHER_LABEL) {
          const text = otherText[qi].trim()
          return text || 'Other'
        }
        return label
      })
    )
    onReply(resolved)
  }, [selected, otherText, onReply])

  const allAnswered = selected.every((a, qi) => {
    if (a.length === 0) return false
    // If "Other" is selected, ensure there's text
    if (a.includes(OTHER_LABEL) && !otherText[qi].trim()) return false
    return true
  })

  return (
    <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 my-2">
      <div className="flex items-start gap-2">
        <HelpCircle size={16} className="text-blue-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 space-y-3">
          {ageDays !== null && (
            <p className="text-[10px] themed-text-muted" data-testid="prompt-age">{t('question.askedAgo', { count: ageDays })}</p>
          )}
          {request.questions.map((q, qi) => {
            const multiple = !!q.multiple
            const isOtherSelected = selected[qi].includes(OTHER_LABEL)

            return (
              <div key={qi}>
                {/* Header chip */}
                {q.header && (
                  <span className="inline-block text-[10px] font-medium uppercase tracking-wide text-blue-400 bg-blue-500/10 rounded px-1.5 py-0.5 mb-1">
                    {q.header}
                  </span>
                )}
                <p className="text-xs font-medium themed-text mb-1.5">{q.question}</p>

                {/* Options */}
                <div className="space-y-1">
                  {q.options.map((opt) => {
                    const isSelected = selected[qi].includes(opt.label)
                    return (
                      <label
                        key={opt.label}
                        className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-500/15 themed-text'
                            : 'hover:bg-blue-500/5 themed-text-secondary'
                        }`}
                      >
                        <input
                          type={multiple ? 'checkbox' : 'radio'}
                          name={`q-${qi}`}
                          checked={isSelected}
                          onChange={() => toggle(qi, opt.label, multiple)}
                          className="mt-0.5 accent-blue-500"
                        />
                        <div className="min-w-0">
                          <span className="text-xs font-medium">{opt.label}</span>
                          {opt.description && (
                            <p className="text-[10px] themed-text-muted mt-0.5 leading-tight">{opt.description}</p>
                          )}
                        </div>
                      </label>
                    )
                  })}

                  {/* "Other" free-text option */}
                  <label
                    className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      isOtherSelected
                        ? 'bg-blue-500/15 themed-text'
                        : 'hover:bg-blue-500/5 themed-text-secondary'
                    }`}
                  >
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      name={`q-${qi}`}
                      checked={isOtherSelected}
                      onChange={() => toggle(qi, OTHER_LABEL, multiple)}
                      className="mt-0.5 accent-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <MessageSquare size={10} className="themed-text-muted" />
                        <span className="text-xs font-medium">Other</span>
                      </div>
                      {isOtherSelected && (
                        <input
                          ref={el => { otherRefs.current[qi] = el }}
                          type="text"
                          value={otherText[qi]}
                          onChange={(e) => {
                            const val = e.target.value
                            setOtherText(prev => {
                              const next = [...prev]
                              next[qi] = val
                              return next
                            })
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && allAnswered) {
                              e.preventDefault()
                              handleSubmit()
                            }
                          }}
                          placeholder="Type your answer..."
                          className="mt-1 w-full text-xs px-2 py-1 rounded themed-input bg-black/10 themed-text"
                          autoFocus
                        />
                      )}
                    </div>
                  </label>
                </div>
              </div>
            )
          })}

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send size={10} />
              Submit
            </button>
            <button
              onClick={onDismiss}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-colors"
            >
              <X size={10} />
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
